import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { StepTimerDef } from "../../shared/recipes";

export type ActiveTimer = {
  id: string;
  stepId: string;
  label: string;
  durationSeconds: number;
  startedAtMs: number;
  pausedAtMs?: number;
  elapsedBeforePauseMs: number;
};

export type TimerAlertSettings = {
  vibration: boolean;
  sound: boolean;
  headerFlash: boolean;
};

const DEFAULT_ALERTS: TimerAlertSettings = {
  vibration: true,
  sound: true,
  headerFlash: true
};

export function loadTimerAlertSettings(): TimerAlertSettings {
  try {
    const raw = localStorage.getItem("cairn.timerAlerts");
    if (raw === null) {
      return DEFAULT_ALERTS;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return DEFAULT_ALERTS;
    }
    const record = parsed as Record<string, unknown>;
    return {
      vibration: record.vibration !== false,
      sound: record.sound !== false,
      headerFlash: record.headerFlash !== false
    };
  } catch {
    return DEFAULT_ALERTS;
  }
}

export function remainingSeconds(timer: ActiveTimer, nowMs: number): number {
  const elapsed =
    timer.pausedAtMs === undefined
      ? timer.elapsedBeforePauseMs + (nowMs - timer.startedAtMs)
      : timer.elapsedBeforePauseMs;
  return Math.max(0, timer.durationSeconds - Math.floor(elapsed / 1000));
}

export function snoozeDurationSeconds(durationSeconds: number): number {
  const extra = Math.round(durationSeconds * 0.2);
  const minutes = Math.round((durationSeconds + extra) / 60);
  return Math.max(1, minutes) * 60;
}

export function useRecipeTimers(sessionId: string) {
  const storageKey = `cairn.timers.${sessionId}`;
  const [timers, setTimers] = useState<ActiveTimer[]>(() => loadTimers(storageKey));
  const [nowMs, setNowMs] = useState(() => Date.now());
  const alertedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    saveTimers(storageKey, timers);
  }, [storageKey, timers]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, []);

  const startTimer = useCallback((stepId: string, def: StepTimerDef) => {
    setTimers((current) => {
      const existing = current.find((timer) => timer.id === def.id);
      if (existing !== undefined) {
        return current;
      }
      return [
        ...current,
        {
          id: def.id,
          stepId,
          label: def.label ?? "Timer",
          durationSeconds: def.durationSeconds,
          startedAtMs: Date.now(),
          elapsedBeforePauseMs: 0
        }
      ];
    });
  }, []);

  const pauseTimer = useCallback((timerId: string) => {
    setTimers((current) =>
      current.map((timer) => {
        if (timer.id !== timerId || timer.pausedAtMs !== undefined) {
          return timer;
        }
        return {
          ...timer,
          pausedAtMs: Date.now(),
          elapsedBeforePauseMs: timer.elapsedBeforePauseMs + (Date.now() - timer.startedAtMs)
        };
      })
    );
  }, []);

  const resumeTimer = useCallback((timerId: string) => {
    setTimers((current) =>
      current.map((timer) => {
        if (timer.id !== timerId || timer.pausedAtMs === undefined) {
          return timer;
        }
        return {
          ...timer,
          startedAtMs: Date.now(),
          pausedAtMs: undefined
        };
      })
    );
  }, []);

  const cancelTimer = useCallback((timerId: string) => {
    setTimers((current) => current.filter((timer) => timer.id !== timerId));
    alertedRef.current.delete(timerId);
  }, []);

  const snoozeTimer = useCallback((timerId: string) => {
    setTimers((current) =>
      current.map((timer) => {
        if (timer.id !== timerId) {
          return timer;
        }
        const nextDuration = snoozeDurationSeconds(timer.durationSeconds);
        alertedRef.current.delete(timerId);
        return {
          ...timer,
          durationSeconds: nextDuration,
          startedAtMs: Date.now(),
          pausedAtMs: undefined,
          elapsedBeforePauseMs: 0
        };
      })
    );
  }, []);

  const completedTimers = useMemo(
    () =>
      timers.filter((timer) => {
        const remaining = remainingSeconds(timer, nowMs);
        return remaining === 0 && timer.pausedAtMs === undefined;
      }),
    [timers, nowMs]
  );

  return {
    timers,
    nowMs,
    startTimer,
    pauseTimer,
    resumeTimer,
    cancelTimer,
    snoozeTimer,
    completedTimers,
    alertedRef
  };
}

function loadTimers(storageKey: string): ActiveTimer[] {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (raw === null) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ActiveTimer[]) : [];
  } catch {
    return [];
  }
}

function saveTimers(storageKey: string, timers: ActiveTimer[]): void {
  sessionStorage.setItem(storageKey, JSON.stringify(timers));
}
